import { Col, Row } from 'antd';
import {
  ArrowRightOutlined,
  DollarOutlined,
  FileTextOutlined,
  RollbackOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { KpiCard } from '@/shared/components/ui';
import { MoneyText } from '@/shared/components/ui';
import { reportsApi } from '@/api/reports';

/**
 * Today's collection rail rendered above the cashier search. Polls the
 * fee-collection report for the {today, today} range so it stays in sync
 * with every receipt taken — refreshed via the global `r` shortcut or the
 * page header refresh button (both invalidate active queries).
 *
 * Sensitive=true on the money tiles wraps them in a `<RevealGroup>` so the
 * cashier can hide totals from over-the-shoulder viewers between students.
 *
 * Defensive parsing: the report DTO returns amounts as strings ("12500.00").
 * We coerce via Number() and let MoneyText handle formatting / NaN fallback.
 */
export function CashierTodayRail() {
  const today = dayjs().format('YYYY-MM-DD');
  const { data } = useQuery({
    queryKey: ['reports', 'fee-collection', today, today, 'rail'],
    queryFn: () => reportsApi.feeCollection(today, today),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  const net = data ? Number(data.netAmount) : 0;
  const gross = data ? Number(data.grossAmount) : 0;
  const reversed = data ? Number(data.reversedAmount) : 0;
  const receipts = data?.receiptCount ?? 0;

  // Top-collected fee type today (by amount). Useful for at-a-glance
  // "everyone's paying tuition today" vs "lab fee blip".
  const topType = (() => {
    if (!data || data.byType.length === 0) return null;
    let best = data.byType[0];
    for (const t of data.byType) {
      if (Number(t.amount) > Number(best.amount)) best = t;
    }
    return best;
  })();

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={12} xl={6}>
        <KpiCard
          label="Net today"
          value={<MoneyText value={net} strong />}
          hint={
            reversed > 0
              ? <span>Gross <MoneyText value={gross} /> · reversed <MoneyText value={reversed} signColor={false} /></span>
              : "Net of reversals"
          }
          icon={<DollarOutlined />}
          tone="success"
          sensitive
        />
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <KpiCard
          label="Receipts today"
          value={receipts.toLocaleString()}
          hint="Number of receipts you've issued today"
          icon={<FileTextOutlined />}
          tone="primary"
        />
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <KpiCard
          label="Top fee type"
          value={topType ? (topType.feeTypeName ?? '—') : '—'}
          hint={topType ? <MoneyText value={Number(topType.amount)} /> : 'No receipts yet today'}
          icon={<TagsOutlined />}
          tone="info"
          sensitive
        />
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <KpiCard
          label={reversed > 0 ? "Reversed today" : "Reversals"}
          value={reversed > 0 ? <MoneyText value={reversed} signColor={false} strong /> : 'None'}
          hint={
            reversed > 0
              ? "Recheck before end-of-day handover"
              : <span>Recorded reversals show here <ArrowRightOutlined style={{ fontSize: 11 }} /></span>
          }
          icon={<RollbackOutlined />}
          tone={reversed > 0 ? 'warning' : 'neutral'}
          sensitive={reversed > 0}
        />
      </Col>
    </Row>
  );
}
